import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { RunTypeBadge } from '../../components/common/RunTypeBadge';
import {
  fmtPct,
  MetricSpec,
  OD_METRICS,
  PC_POG_METRICS,
  PC_ROG_METRICS,
  PR_METRICS,
} from '../../metrics';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalGtDataset, MetricsOverviewResponse, RunType } from '../../types';
import { fmtIso } from '../../utils';

// ─── helpers ─────────────────────────────────────────────────────────────────

function buildColGroups(
  metrics: MetricSpec[]
): Array<{ cls: string | undefined; items: MetricSpec[] }> {
  const groups: Array<{ cls: string | undefined; items: MetricSpec[] }> = [];
  for (const m of metrics) {
    const last = groups[groups.length - 1];
    if (last && last.cls === m.classGroup) last.items.push(m);
    else groups.push({ cls: m.classGroup, items: [m] });
  }
  return groups;
}

// ─── shared header styles ────────────────────────────────────────────────────

const sectionHeaderStyle: React.CSSProperties = {
  padding: '6px 12px',
  textAlign: 'center',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border)',
  background: 'var(--table-header-bg)',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

const classHeaderStyle: React.CSSProperties = {
  padding: '5px 8px',
  textAlign: 'center',
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--divider)',
  background: 'var(--surface-raised)',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

// ─── cell components ──────────────────────────────────────────────────────────

function Cell({
  value,
  best,
  fmt: fmtFn = fmtPct,
  lowerIsBetter,
  sectionStart,
}: {
  value: number | null | undefined;
  best: number | null;
  fmt?: (v: number | null | undefined) => string;
  lowerIsBetter?: boolean;
  sectionStart?: boolean;
}) {
  const isAccent =
    value != null &&
    best != null &&
    value === best &&
    (lowerIsBetter ? value <= best : value >= best);
  return (
    <td
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 12,
        color: isAccent ? 'var(--accent)' : 'var(--text-primary)',
        fontWeight: isAccent ? 700 : 400,
        padding: '8px 12px',
        borderBottom: '1px solid var(--divider)',
        borderLeft: sectionStart ? '2px solid var(--border)' : undefined,
        textAlign: 'right',
      }}
    >
      {fmtFn(value)}
    </td>
  );
}

const RUN_TYPES: RunType[] = ['OD', 'PR', 'PC', 'Full'];

export const MetricsPage: React.FC = () => {
  const { token } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const datasetId = searchParams.get('dataset_id') ?? '';

  const [datasets, setDatasets] = useState<EvalGtDataset[]>([]);
  const [metrics, setMetrics] = useState<MetricsOverviewResponse | null>(null);
  const [runTypeFilter, setRunTypeFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    EvalApiService.listDatasets(token).then((r) => setDatasets(r.items));
  }, [token]);

  const loadMetrics = useCallback(
    (dsId: string) => {
      if (!dsId) return;
      setLoading(true);
      EvalApiService.getMetrics(token, dsId, runTypeFilter || undefined)
        .then(setMetrics)
        .finally(() => setLoading(false));
    },
    [token, runTypeFilter]
  );

  useEffect(() => {
    loadMetrics(datasetId);
  }, [datasetId, loadMetrics]);

  const handleDatasetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSearchParams(val ? { dataset_id: val } : {});
    setMetrics(null);
  };

  const rows = metrics?.rows ?? [];
  const filteredRows = runTypeFilter ? rows.filter((r) => r.run_type === runTypeFilter) : rows;

  const hasOD = filteredRows.some((r) => r.result?.OD);
  const hasPR = filteredRows.some((r) => r.result?.PR);
  const hasPC = filteredRows.some((r) => r.result?.PC);

  const odCols = hasOD ? OD_METRICS : [];
  const prCols = hasPR ? PR_METRICS : [];
  const pcPogCols = hasPC ? PC_POG_METRICS : [];
  const pcRogCols = hasPC ? PC_ROG_METRICS : [];

  const allCols: MetricSpec[] = [...odCols, ...prCols, ...pcPogCols, ...pcRogCols];

  const bestValues = new Map<string, number | null>(
    allCols.map((m) => {
      const vals = filteredRows
        .map((r) => (r.result ? m.getValue(r.result) : null))
        .filter((v): v is number => v != null);
      const best = vals.length ? (m.lowerIsBetter ? Math.min(...vals) : Math.max(...vals)) : null;
      return [m.label, best];
    })
  );

  // OD and PR both have multi-metric class groups → need a 3rd header row for metric labels.
  // PC metrics have no class grouping, so PC-only datasets only need 2 header rows.
  const hasRow3 = hasOD || hasPR;
  const infoRowSpan = hasRow3 ? 3 : 2;

  // Labels of the first column in each section/sub-section — used to draw left-border dividers.
  const sectionStartLabels = new Set<string>([
    ...(odCols.length ? [odCols[0].label] : []),
    ...(prCols.length ? [prCols[0].label] : []),
    ...(pcPogCols.length ? [pcPogCols[0].label] : []),
    ...(pcRogCols.length ? [pcRogCols[0].label] : []),
  ]);

  return (
    <div className="page-content">
      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">Metrics Overview</div>
          <div className="eval-page-sub">
            Aggregated results for all completed runs on a dataset
          </div>
        </div>
      </div>

      <div className="eval-filters" style={{ marginBottom: 24 }}>
        <select
          className="eval-filter-select"
          value={datasetId}
          onChange={handleDatasetChange}
          style={{ minWidth: 220 }}
        >
          <option value="">Select a dataset…</option>
          {datasets.map((d) => (
            <option key={d.dataset_id} value={d.dataset_id}>
              {d.dataset_name}
              {d.status === 'locked' ? ' (locked)' : ' (draft)'}
            </option>
          ))}
        </select>

        <select
          className="eval-filter-select"
          value={runTypeFilter}
          onChange={(e) => setRunTypeFilter(e.target.value)}
          disabled={!datasetId}
        >
          <option value="">All run types</option>
          {RUN_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {!datasetId && (
        <div
          style={{
            padding: '48px 0',
            textAlign: 'center',
            color: 'var(--text-tertiary)',
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
          }}
        >
          Select a dataset above to view metrics
        </div>
      )}

      {datasetId && loading && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading…</div>
      )}

      {datasetId && !loading && metrics && filteredRows.length === 0 && (
        <div
          style={{
            padding: '48px 0',
            textAlign: 'center',
            color: 'var(--text-tertiary)',
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
          }}
        >
          No completed runs yet for{' '}
          <span style={{ color: 'var(--text-secondary)' }}>{metrics.dataset_name}</span>
        </div>
      )}

      {filteredRows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 12,
              background: 'var(--surface)',
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid var(--border)',
            }}
          >
            <thead>
              {/* Row 1 — section headers */}
              <tr style={{ background: 'var(--table-header-bg)' }}>
                <Th rowSpan={infoRowSpan}>Run</Th>
                <Th rowSpan={infoRowSpan}>Type</Th>
                <Th rowSpan={infoRowSpan}>OD Model</Th>
                <Th rowSpan={infoRowSpan}>PR Model</Th>
                <Th rowSpan={infoRowSpan}>PC Model</Th>
                <Th rowSpan={infoRowSpan}>Triggered</Th>
                <Th rowSpan={infoRowSpan}>By</Th>
                {hasOD && (
                  <th
                    colSpan={odCols.length}
                    style={{ ...sectionHeaderStyle, borderLeft: '2px solid var(--border)' }}
                  >
                    OD
                  </th>
                )}
                {hasPR && (
                  <th
                    colSpan={prCols.length}
                    style={{ ...sectionHeaderStyle, borderLeft: '2px solid var(--border)' }}
                  >
                    PR
                  </th>
                )}
                {hasPC && (
                  <>
                    <th
                      colSpan={pcPogCols.length}
                      style={{ ...sectionHeaderStyle, borderLeft: '2px solid var(--border)' }}
                    >
                      PC — POG
                    </th>
                    <th
                      colSpan={pcRogCols.length}
                      style={{ ...sectionHeaderStyle, borderLeft: '2px solid var(--border)' }}
                    >
                      PC — ROG
                    </th>
                  </>
                )}
              </tr>

              {/* Row 2 — class headers */}
              <tr>
                {hasOD &&
                  buildColGroups(odCols).map(({ cls, items }, gi) => {
                    const bl = gi === 0 ? '2px solid var(--border)' : undefined;
                    if (cls === undefined || items.length === 1) {
                      return (
                        <th
                          key={items[0].label}
                          rowSpan={2}
                          style={{ ...classHeaderStyle, borderLeft: bl }}
                        >
                          {cls ?? items[0].label}
                        </th>
                      );
                    }
                    return (
                      <th
                        key={cls}
                        colSpan={items.length}
                        style={{ ...classHeaderStyle, borderLeft: bl }}
                      >
                        {cls}
                      </th>
                    );
                  })}
                {hasPR &&
                  buildColGroups(prCols).map(({ cls, items }, gi) => {
                    const bl = gi === 0 ? '2px solid var(--border)' : undefined;
                    if (cls === undefined || items.length === 1) {
                      return (
                        <th
                          key={items[0].label}
                          rowSpan={2}
                          style={{ ...classHeaderStyle, borderLeft: bl }}
                        >
                          {cls ?? items[0].label}
                        </th>
                      );
                    }
                    return (
                      <th
                        key={cls}
                        colSpan={items.length}
                        style={{ ...classHeaderStyle, borderLeft: bl }}
                      >
                        {cls}
                      </th>
                    );
                  })}
                {hasPC &&
                  [...pcPogCols, ...pcRogCols].map((m, mi) => {
                    const bl =
                      mi === 0 || mi === pcPogCols.length ? '2px solid var(--border)' : undefined;
                    return (
                      <th
                        key={m.label}
                        rowSpan={hasRow3 ? 2 : 1}
                        style={{ ...classHeaderStyle, borderLeft: bl }}
                      >
                        {m.shortLabel ?? m.label}
                      </th>
                    );
                  })}
              </tr>

              {/* Row 3 — metric labels for multi-metric class groups (OD / PR only) */}
              {hasRow3 && (
                <tr>
                  {hasOD &&
                    buildColGroups(odCols).flatMap(({ cls, items }) =>
                      cls !== undefined && items.length > 1
                        ? items.map((m) => (
                            <Th
                              key={m.label}
                              right
                              style={
                                sectionStartLabels.has(m.label)
                                  ? { borderLeft: '2px solid var(--border)' }
                                  : undefined
                              }
                            >
                              {m.shortLabel ?? m.label}
                            </Th>
                          ))
                        : []
                    )}
                  {hasPR &&
                    buildColGroups(prCols).flatMap(({ cls, items }) =>
                      cls !== undefined && items.length > 1
                        ? items.map((m) => (
                            <Th
                              key={m.label}
                              right
                              style={
                                sectionStartLabels.has(m.label)
                                  ? { borderLeft: '2px solid var(--border)' }
                                  : undefined
                              }
                            >
                              {m.shortLabel ?? m.label}
                            </Th>
                          ))
                        : []
                    )}
                  {/* PC cols are covered by rowSpan=2 in row 2 */}
                </tr>
              )}
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.run_id} className="eval-metrics-row">
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--divider)' }}>
                    <Link
                      to={`/eval/runs/${row.run_id}`}
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 12,
                        color: 'var(--accent)',
                        textDecoration: 'none',
                      }}
                    >
                      {row.run_id}
                    </Link>
                  </td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--divider)' }}>
                    <RunTypeBadge runType={row.run_type} />
                  </td>
                  <ModelVersionCell version={row.model_versions.OD} />
                  <ModelVersionCell version={row.model_versions.PR} />
                  <ModelVersionCell version={row.model_versions.PC} />
                  <td
                    style={{
                      padding: '8px 12px',
                      borderBottom: '1px solid var(--divider)',
                      color: 'var(--text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fmtIso(row.triggered_at)}
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      borderBottom: '1px solid var(--divider)',
                      color: 'var(--text-secondary)',
                      maxWidth: 140,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.triggered_by.split('@')[0]}
                  </td>
                  {allCols.map((m) => (
                    <Cell
                      key={m.label}
                      value={row.result ? m.getValue(row.result) : null}
                      best={bestValues.get(m.label) ?? null}
                      fmt={m.fmt}
                      lowerIsBetter={m.lowerIsBetter}
                      sectionStart={sectionStartLabels.has(m.label)}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {filteredRows.length} run{filteredRows.length !== 1 ? 's' : ''} ·{' '}
            <span style={{ color: 'var(--accent)' }}>highlighted</span> = best value per column
          </div>
        </div>
      )}
    </div>
  );
};

const Th: React.FC<{
  children: React.ReactNode;
  right?: boolean;
  rowSpan?: number;
  colSpan?: number;
  style?: React.CSSProperties;
}> = ({ children, right, rowSpan, colSpan, style }) => (
  <th
    rowSpan={rowSpan}
    colSpan={colSpan}
    style={{
      padding: '8px 12px',
      textAlign: right ? 'right' : 'left',
      color: 'var(--text-secondary)',
      fontWeight: 600,
      fontSize: 11,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      borderBottom: '1px solid var(--border)',
      whiteSpace: 'nowrap',
      background: 'var(--table-header-bg)',
      verticalAlign: 'middle',
      ...style,
    }}
  >
    {children}
  </th>
);

const ModelVersionCell: React.FC<{ version: string | null }> = ({ version }) => (
  <td
    style={{
      padding: '8px 12px',
      borderBottom: '1px solid var(--divider)',
      fontFamily: 'var(--font-mono)',
      fontSize: 11,
      color: version ? 'var(--text-primary)' : 'var(--text-tertiary)',
    }}
  >
    {version ?? '—'}
  </td>
);
